import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { User } from '../types';
import { AuthResponse } from '../api/auth.api';

export const TOKEN_KEY = 'arena_token';
const USER_KEY = 'arena_user';
const STATUS_KEY = 'arena_auth_status';
const IS_ADMIN_KEY = 'arena_is_admin';

type AuthStatus = 'approved' | 'pending' | 'none';

type AuthContextType = {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isReady: boolean;
  status: AuthStatus;
  login: (payload: AuthResponse) => void;
  setPending: () => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const parseJwt = (token: string) => {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

const getTokenExpiry = (token: string) => {
  const payload = parseJwt(token);
  if (!payload?.exp) {
    return null;
  }
  return payload.exp * 1000;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<number | null>(null);
  const [status, setStatus] = useState<AuthStatus>('none');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(STATUS_KEY);
    localStorage.removeItem(IS_ADMIN_KEY);
    setUser(null);
    setToken(null);
    setTokenExpiry(null);
    setStatus('none');
    setIsAdmin(false);
  };

  const login = (payload: AuthResponse) => {
    localStorage.setItem(TOKEN_KEY, payload.token);
    localStorage.setItem(USER_KEY, JSON.stringify(payload.user));
    localStorage.setItem(STATUS_KEY, 'approved');
    if (payload.isAdmin) {
      localStorage.setItem(IS_ADMIN_KEY, 'true');
      setIsAdmin(true);
    } else {
      localStorage.removeItem(IS_ADMIN_KEY);
      setIsAdmin(false);
    }
    setUser(payload.user);
    setToken(payload.token);
    setTokenExpiry(getTokenExpiry(payload.token));
    setStatus('approved');
  };

  const setPending = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.setItem(STATUS_KEY, 'pending');
    localStorage.removeItem(IS_ADMIN_KEY);
    setUser(null);
    setToken(null);
    setTokenExpiry(null);
    setStatus('pending');
    setIsAdmin(false);
  };

  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);
    const storedStatus = localStorage.getItem(STATUS_KEY) as AuthStatus | null;
    const storedIsAdmin = localStorage.getItem(IS_ADMIN_KEY) === 'true';

    if (storedToken) {
      const expiry = getTokenExpiry(storedToken);
      if (expiry && expiry <= Date.now()) {
        logout();
      } else {
        setToken(storedToken);
        setTokenExpiry(expiry);
        setStatus('approved');
        setIsAdmin(storedIsAdmin);
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }
      }
    } else if (storedStatus === 'pending') {
      setStatus('pending');
      setIsAdmin(false);
    }

    setIsReady(true);
  }, []);

  useEffect(() => {
    if (!tokenExpiry) {
      return undefined;
    }

    const timeout = tokenExpiry - Date.now();
    if (timeout <= 0) {
      logout();
      return undefined;
    }

    const timer = window.setTimeout(() => logout(), timeout);
    return () => window.clearTimeout(timer);
  }, [tokenExpiry]);

  useEffect(() => {
    const handleLogout = () => logout();
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(token),
      isAdmin,
      isReady,
      status,
      login,
      setPending,
      logout,
    }),
    [user, token, isAdmin, isReady, status]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
